import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { maskPostalCode, SHIPPING_COUNTRIES, type ShippingField, type ShippingForm } from '@/lib/checkout/shipping'

interface ShippingAddressFieldsProps {
  value: ShippingForm
  /** i18n keys (namespace `products`) per field, or the API's own message. */
  errors: Partial<Record<ShippingField, string | undefined>>
  onChange: (field: ShippingField, value: string) => void
  disabled?: boolean
  className?: string
}

/**
 * Where a physical product is sent (F-74). Brazil is the default and takes a
 * CEP mask; any other country takes its postal code as typed. The API checks
 * the format of each country, and its message lands under the field.
 */
export function ShippingAddressFields({ value, errors, onChange, disabled, className }: ShippingAddressFieldsProps) {
  const { t, i18n } = useTranslation('products')
  const errorText = (error: string) => (i18n.exists(error, { ns: 'products' }) ? t(error) : error)
  const regionNames = (() => {
    try {
      return new Intl.DisplayNames([i18n.language || 'en'], { type: 'region' })
    } catch {
      return null
    }
  })()

  const field = (name: ShippingField, span: string, extra: Record<string, unknown> = {}) => (
    <div className={cn('min-w-0', span)}>
      <label htmlFor={`ship-${name}`} className="mb-1 block text-xs text-gray-500">
        {t(`shipping.fields.${name}`)}
      </label>
      <Input
        id={`ship-${name}`}
        data-testid={`ship-${name}`}
        value={value[name]}
        onChange={(e) => onChange(name, name === 'postalCode' ? maskPostalCode(value.country, e.target.value) : e.target.value)}
        disabled={disabled}
        aria-invalid={errors[name] ? true : undefined}
        aria-describedby={errors[name] ? `ship-${name}-error` : undefined}
        className={cn('h-11 bg-white', errors[name] && 'border-red-500')}
        {...extra}
      />
      {errors[name] && (
        <p id={`ship-${name}-error`} className="mt-1 text-xs text-red-600">{errorText(errors[name] as string)}</p>
      )}
    </div>
  )

  return (
    <div className={cn('grid grid-cols-1 gap-3 sm:grid-cols-6', className)}>
      <div className="min-w-0 sm:col-span-2">
        <label htmlFor="ship-country" className="mb-1 block text-xs text-gray-500">{t('shipping.fields.country')}</label>
        <select
          id="ship-country"
          data-testid="ship-country"
          value={value.country}
          onChange={(e) => onChange('country', e.target.value)}
          disabled={disabled}
          className="h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {SHIPPING_COUNTRIES.map((code) => (
            <option key={code} value={code}>{regionNames?.of(code) || code}</option>
          ))}
        </select>
      </div>
      {field('postalCode', 'sm:col-span-2', { autoComplete: 'postal-code', inputMode: value.country === 'BR' ? 'numeric' : 'text', placeholder: value.country === 'BR' ? '00000-000' : '' })}
      {field('phone', 'sm:col-span-2', { autoComplete: 'tel', inputMode: 'tel' })}
      {field('name', 'sm:col-span-6', { autoComplete: 'name' })}
      {field('street', 'sm:col-span-4', { autoComplete: 'address-line1' })}
      {field('number', 'sm:col-span-2')}
      {field('complement', 'sm:col-span-3', { autoComplete: 'address-line2' })}
      {field('district', 'sm:col-span-3')}
      {field('city', 'sm:col-span-4', { autoComplete: 'address-level2' })}
      {field('state', 'sm:col-span-2', { autoComplete: 'address-level1' })}
    </div>
  )
}
